/**
 * World 2 — "Sunken Bay" (Water, levels 11–20). Act I, chapter 2: a bell
 * beneath the bay tolls on its own, and every toll pulls the tide higher. An
 * ancient Fernail — Tidebell — rings it, calling for a Keeper who never
 * returns. Levels/caps stamped from World 2's band (11→20). Finale boss (slot 0)
 * is Fernail, named Tidebell.
 */

import {
  node,
  giftNode,
  giftTracks,
  giftXp,
  rival,
  CAMPAIGN_XP_NORMAL,
  CAMPAIGN_XP_MINI_BOSS,
  CAMPAIGN_XP_ZONE_BOSS,
  AQUA,
  YETI,
  PINKY,
  SUIMON,
  FERNAIL,
  TRIXON,
  TALION,
  PEFAREON,
  type CampaignWorld,
} from './builders.js';
import { GIFT_SKILL_SUIKA } from '../cardShopConfig.js';
import { NODE_STORIES, ZONE_STORIES, WORLD_STORIES } from '../campaignStories.js';

const ZONE_4_NODES = [
  node({
    nodeId: 'z4-n1',
    kind: 'normal',
    rivalName: 'Mimi',
    rivalTitle: 'Lass',
    ...NODE_STORIES['z4-n1'],
    squad: [rival(FERNAIL, 'Barnacle'), rival(TRIXON, 'Squall'), rival(PINKY, 'Bubbles')],
    opponentCount: 1,
    xpReward: CAMPAIGN_XP_NORMAL,
    portraitSpeciesId: FERNAIL,
  }),
  giftNode({
    nodeId: 'z4-g1',
    giver: 'Bay Fisher',
    label: 'Trailside Gift',
    story: 'A fisher mending a net on the shore calls you over.',
    line: 'Caught more than fish today. Soul Shards! Take them.',
    reward: { tracks: giftTracks(FERNAIL) },
    portraitSpeciesId: FERNAIL,
  }),
  node({
    nodeId: 'z4-n2',
    kind: 'normal',
    rivalName: 'Lulu',
    rivalTitle: 'Lass',
    ...NODE_STORIES['z4-n2'],
    squad: [rival(PINKY, 'Coral'), rival(AQUA, 'Ripple'), rival(SUIMON, 'Murkfin')],
    opponentCount: 1,
    xpReward: CAMPAIGN_XP_NORMAL,
    portraitSpeciesId: PINKY,
  }),
  giftNode({
    nodeId: 'z4-g2',
    giver: 'Tide Elder',
    label: 'Training Charm',
    story: "An elder watching three baby Suimon splash in the pools waves you closer.",
    line: 'This charm keeps young hearts calm. Here.',
    reward: { xp: giftXp(1) },
    portraitSpeciesId: FERNAIL,
  }),
  node({
    nodeId: 'z4-n3',
    kind: 'normal',
    encounterKind: 'wild',
    rivalName: 'Frostfin',
    rivalTitle: 'Wild Suikamon',
    ...NODE_STORIES['z4-n3'],
    squad: [rival(YETI, 'Frostling'), rival(FERNAIL, 'Lantern'), rival(AQUA, 'Beacon')],
    opponentCount: 1,
    xpReward: CAMPAIGN_XP_NORMAL,
    portraitSpeciesId: YETI,
  }),
  node({
    nodeId: 'z4-n4',
    kind: 'normal',
    rivalName: 'Zip',
    rivalTitle: 'Clan Z',
    ...NODE_STORIES['z4-n4'],
    squad: [rival(TALION, 'Dash'), rival(SUIMON, 'Brine'), rival(PINKY, 'Pearl')],
    opponentCount: 1,
    xpReward: CAMPAIGN_XP_NORMAL,
    portraitSpeciesId: TALION,
  }),
  node({
    nodeId: 'z4-nw1',
    kind: 'normal',
    encounterKind: 'wild',
    rivalName: 'Reefmaw',
    rivalTitle: 'Wild Suikamon',
    ...NODE_STORIES['z4-nw1'],
    squad: [rival(AQUA, 'Surge'), rival(PINKY, 'Coral'), rival(SUIMON, 'Silt')],
    opponentCount: 1,
    xpReward: CAMPAIGN_XP_NORMAL,
    portraitSpeciesId: AQUA,
  }),
  node({
    nodeId: 'z4-n5',
    kind: 'mini_boss',
    rivalName: 'Aggie',
    rivalTitle: 'Medium',
    ...NODE_STORIES['z4-n5'],
    squad: [rival(SUIMON, 'Tempest'), rival(TRIXON, 'Gale'), rival(YETI, 'Hailstone')],
    opponentCount: 2,
    xpReward: CAMPAIGN_XP_MINI_BOSS,
    portraitSpeciesId: SUIMON,
    soulSpeciesId: SUIMON,
  }),
  node({
    nodeId: 'z4-n6',
    kind: 'normal',
    rivalName: 'Cliff',
    rivalTitle: 'Bird Keeper',
    ...NODE_STORIES['z4-n6'],
    squad: [rival(PINKY, 'Coralback'), rival(AQUA, 'Drift'), rival(TRIXON, 'Eddy')],
    opponentCount: 2,
    xpReward: CAMPAIGN_XP_NORMAL,
    portraitSpeciesId: AQUA,
  }),
  node({
    nodeId: 'z4-n7',
    kind: 'zone_boss',
    rivalName: 'Mimi',
    rivalTitle: 'Lass',
    ...NODE_STORIES['z4-n7'],
    squad: [rival(AQUA, 'Tidecaller'), rival(FERNAIL, 'Bastion'), rival(TRIXON, 'Leviathan')],
    opponentCount: 2,
    xpReward: CAMPAIGN_XP_ZONE_BOSS,
    portraitSpeciesId: AQUA,
  }),
];

const ZONE_5_NODES = [
  node({
    nodeId: 'z5-n1',
    kind: 'normal',
    rivalName: 'Lulu',
    rivalTitle: 'Lass',
    ...NODE_STORIES['z5-n1'],
    squad: [rival(SUIMON, 'Echo'), rival(PINKY, 'Glow'), rival(AQUA, 'Murk')],
    opponentCount: 2,
    xpReward: CAMPAIGN_XP_NORMAL,
    portraitSpeciesId: SUIMON,
  }),
  giftNode({
    nodeId: 'z5-g1',
    giver: 'Shrine Keeper',
    label: 'Soul Token',
    story: 'Beneath the water at your feet, a tide shrine hums.',
    line: 'The tide leaves a big handful of Soul Shards here. Take them!',
    reward: { tracks: giftTracks(FERNAIL, true), suika: GIFT_SKILL_SUIKA },
    portraitSpeciesId: FERNAIL,
  }),
  node({
    nodeId: 'z5-n2',
    kind: 'normal',
    rivalName: 'Timmy',
    rivalTitle: 'Youngster',
    ...NODE_STORIES['z5-n2'],
    squad: [rival(YETI, 'Glimmerfrost'), rival(FERNAIL, 'Angler'), rival(TRIXON, 'Lure')],
    opponentCount: 2,
    xpReward: CAMPAIGN_XP_NORMAL,
    portraitSpeciesId: YETI,
  }),
  node({
    nodeId: 'z5-n3',
    kind: 'normal',
    rivalName: 'Zap',
    rivalTitle: 'Clan Z',
    ...NODE_STORIES['z5-n3'],
    squad: [rival(PEFAREON, 'Shade'), rival(SUIMON, 'Grapnel'), rival(AQUA, 'Silt')],
    opponentCount: 3,
    xpReward: CAMPAIGN_XP_NORMAL,
    portraitSpeciesId: PEFAREON,
  }),
  node({
    nodeId: 'z5-n4',
    kind: 'normal',
    rivalName: 'Aggie',
    rivalTitle: 'Medium',
    ...NODE_STORIES['z5-n4'],
    squad: [rival(SUIMON, 'Knell'), rival(PINKY, 'Palelight'), rival(YETI, 'Glacier')],
    opponentCount: 3,
    xpReward: CAMPAIGN_XP_NORMAL,
    portraitSpeciesId: SUIMON,
  }),
  node({
    nodeId: 'z5-n5',
    kind: 'mini_boss',
    rivalName: 'Vera',
    rivalTitle: 'Channeler',
    ...NODE_STORIES['z5-n5'],
    squad: [rival(AQUA, 'Maelstrom'), rival(SUIMON, 'Abyss'), rival(FERNAIL, 'Rampart')],
    opponentCount: 3,
    xpReward: CAMPAIGN_XP_MINI_BOSS,
    portraitSpeciesId: AQUA,
    soulSpeciesId: AQUA,
  }),
  node({
    nodeId: 'z5-n6',
    kind: 'zone_boss',
    rivalName: 'Tidebell',
    rivalTitle: 'Bell Guardian',
    ...NODE_STORIES['z5-n6'],
    squad: [
      rival(FERNAIL, 'Tidebell'),
      rival(SUIMON, 'Brinemaw'),
      rival(TRIXON, 'Riptide'),
    ],
    opponentCount: 3,
    xpReward: CAMPAIGN_XP_ZONE_BOSS,
    portraitSpeciesId: FERNAIL,
    soulSpeciesId: FERNAIL,
  }),
];

export const WORLD_2: CampaignWorld = {
  worldId: 'w2',
  name: 'Sunken Bay',
  element: 'Water',
  featuredSpeciesId: FERNAIL,
  story: WORLD_STORIES['w2'],
  locked: true,
  zones: [
    {
      zoneId: 'z4',
      name: 'Tide Crossing',
      element: 'Water',
      story: ZONE_STORIES['z4'],
      nodes: ZONE_4_NODES,
    },
    {
      zoneId: 'z5',
      name: 'Drowned Deep',
      element: 'Water',
      story: ZONE_STORIES['z5'],
      nodes: ZONE_5_NODES,
    },
  ],
};
